/**
 * Componente que sirve de Adapter para hacer Búsquedas en Elastic Search
 * Version basada en functions
 * Version: 2022-08-11
 */
import { Inject, Injectable } from '@angular/core';
import { Functions, FunctionsInstances, getFunctions, httpsCallableData } from '@angular/fire/functions';
import { format } from 'date-fns';
import { firstValueFrom, map, Observable } from 'rxjs';

export interface ESPaginator {
  pageIndex: number;
  pageSize: number;
}

export interface ESSort {
  active: string;
  direction: 'asc' | 'desc' | '';
}

export interface ESExtras {
  [key: string] : PropertyExtra
}

export interface PropertyExtra {
  type: 'wildcard' | 'text' | 'term' | 'dateRange',
  /**
   * If is a nested property, indicate the Parent property name;
   */
  nested? : string;
}

export interface ElasticBody {
  query: any;
  from: any;
  size: any;
  sort: any;
  _source?: Array<string>
}

@Injectable()
export class ElasticSearchService{
  functions:Functions;
  constructor(
    @Inject('FIREBASE_REGION') public FIREBASE_REGION: string
  ) {
    this.functions = getFunctions(undefined, FIREBASE_REGION);
  }

/**
   * Buscar por Indice en Elastic Search
   * @param index Indice de la "colección"
   * @param body Parámetros de Busqueda en formato ElasticSearch
   * @param serialize (Opcional) Serializa los datos que llegan de elastic search y transforma a como lo necesitamos.
   * @param options (Opcional) Permite especificar opciones adicionales.. si se envía body:true devuelve elbody del query
   */
  query$(index, body, serialize?, options?):Observable<any>{

    if(!index || !body) Promise.reject("ES Error,Invalid Parameters");
    let path = `api/search`;
    return httpsCallableData(this.functions , path, { timeout: 60_000 })({index, body})
      .pipe(
        map((result:any)=>{
          let response = result;
          let data = response.hits.hits.map(item =>{
              const id = item._id;
              //Serializamos por si en la consulta hay datos que transformar, lo hará cada responsable
              let source = serialize ? serialize(item._source): item._source;
              return {id, ...source};
            })
          let _meta = {
            count:response.hits.total.value,
            body:null
          }
          if(options?.body) _meta.body = body;
          return {data,_meta}
        })
      );
  }

  query(index, body, serialize?, options?):Promise<any>{
    return firstValueFrom(this.query$(index, body, serialize, options));
  }



    // return this.a2f({index, body})
    // .then((result:any)=>{
    //   let response = result;
    //   let data = response.hits.hits.map(item =>{
    //       const id = item._id;
    //       //Serializamos por si en la consulta hay datos que transformar, lo hará cada responsable
    //       let source = serialize ? serialize(item._source): item._source;
    //       return {id, ...source};
    //     })
    //   let _meta = {
    //     count:response.hits.total.value,
    //     body:null
    //   }
    //   if(options?.body) _meta.body = body;
    //   return {data,_meta}
    // })
    // .catch(err=>console.error("Es error: ", err));
  //}

  /**
   *  para convertir los datos que recibimos de forma estandar desde MatTable, quien use ES debe llamar esta función para hacer el cambio
   * @param {ESPaginator} _paginator
   * @param {ESSort}_sort
   * @param _filter
   * @param { ESExtras} extras :  Optional
   *  Ejemplo de Extras
   * let extras = {
   *  email:{
   *    type:"wildcard"
   *  },
   *  displayName:{
   *    type:"text"
   *  },
   *   vehicles_idType:{
   *     nested:"vehicles"
   *   },
   *   vehicles_year:{
   *     nested:"vehicles"
   *   },
   *   vehicles_color:{
   *     nested:"vehicles"
   *   },
   *   vehicles_idBrand:{
   *     nested:"vehicles"
   *   },
   *   vehicles_numberPlate:{
   *     nested:"vehicles",
   *     type:"wildcard"
   *   }
   * }
   * @param { Array<string>} source : Optional, array of properties to be included.

   * @returns Body en formato Elastic Search
   */

  mtToBody(_paginator: ESPaginator, _sort: ESSort, _filter, extras?: ESExtras, source?:Array<string>){
    let from = _paginator.pageIndex * _paginator.pageSize;
    let size = _paginator.pageSize;
    let queryTerm = this._getFilter(_filter, extras);
    //Armamos el Sort
    let sort ={}
    if (_sort.active && _sort.direction.length){
      let index = _sort.active;
      sort = [{
        [index] :{"order":_sort.direction}
      }]
    }else{
      sort= {} //Default
    }
    let query =  {
      //"match_all": {}
      bool:queryTerm
    }
    const body:ElasticBody = {query, from, size, sort}
    if (source){
      body._source = source;
    }
    return body;
  }

  //Esto transforma el a2filter a filter es
  private _getFilter(filters, extras?){
    const query = {
      must:[],
      filter:[],
      must_not:[]
    }
    Object.keys(filters).forEach(_key => {
      const type = (extras && extras[_key] && extras[_key].type) ? extras[_key].type : 'term';
      const queryTerm = {
        must:[],
        filter:[],
        must_not:[]
      }
      filters[_key].forEach(filter => {
        let values = Array.isArray(filter.value) ? filter.value : [filter.value];
        let op = filter.op;
        let key = filter.key;
        let queryOption = op=='!==' ? 'must_not' : 'filter';
        values.forEach(value => {
          switch (type) {
            case 'term':
              queryTerm[queryOption].push(this._getTerm(key, value));
              break;
            case 'wildcard':
              queryTerm[queryOption].push(this._getWilcard(key, value));
              break;
            case 'dateRange':
              queryTerm[queryOption].push(this._getDateRange(key, value));
              break;
            case 'text':
              queryOption = op=='!==' ? 'must_not' : 'must';
              queryTerm[queryOption].push(this._getText(key, value));
              break;
          }
        });
      })

      //si cualquiera de los elementso dentro de queryTerm tiene mas de uno se convierte en
      Object.keys(queryTerm).forEach(key =>{
        if (queryTerm[key].length > 1){
          queryTerm[key] = [{
            "bool":{
              "should" : queryTerm[key],
              "minimum_should_match": 1
            }
          }]
        }
      })

      //verificamos si es nested
      if (extras && extras[_key] && extras[_key].nested){
        Object.keys(queryTerm).forEach(key =>{
          if (queryTerm[key].length){
            queryTerm[key] =[{
              nested:{
                path:  extras[_key].nested,
                query:{
                  bool:{
                    filter:queryTerm[key]
                  }
                }
              }
            }]
          }
        })
      }

      //Asignamos al query principal los querys de la propiedad al apartado que pertenece
      Object.keys(query).forEach(key => query[key].push(...queryTerm[key]));
    })
    return query;
  }

  private _getWilcard(key, value){
    return {"wildcard" : { [key] :/[*?]/.test(value) ? value : '*' + value + '*'}};
  }

  private _getDateRange(key, value){
    const _format = 'yyyy-MM-dd'
    const fi = format(value.fi, _format);
    const fe = format(value.fe, _format);
    return {range: {
        [key+'._seconds']: {
          "format": _format,
          "time_zone": Intl.DateTimeFormat().resolvedOptions().timeZone, //"Europe/Madrid",
          "gte": fi + "||/d",
          "lte": fe + "||/d"
        }
      }
    }
  }

  private _getTerm(key, value){
    return  {"term" : {[key] : (typeof value === 'object') ? value.id : value }};
  }

  private _getText(key, value){
    const match = value.split(" ").length;
    return  {"match_bool_prefix" : {[key] : {"query":value, "minimum_should_match": match} }};
  }

}
